import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, AlertTriangle, Lock, ChevronRight, ChevronLeft, Save, Shield } from 'lucide-react';
import { ProductSelector } from '../components/ProductSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'client', label: 'About You' },
  { id: 'disclosures', label: 'Disclosures' },
  { id: 'fact_find', label: 'Your Finances' },
  { id: 'needs_analysis', label: 'Your Goals' },
  { id: 'investor_profile', label: 'Investor Profile' },
  { id: 'review', label: 'Review & Submit' },
];

const ClientIntake = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intake, setIntake] = useState(null);
  const [caseSummary, setCaseSummary] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [geo, setGeo] = useState({ status: 'idle', lat: null, lng: null, accuracy: null });

  // section-snapshots
  const [clientData, setClientData] = useState({});
  const [factFindData, setFactFindData] = useState({});
  const [needsData, setNeedsData] = useState({});
  const [investorData, setInvestorData] = useState({});
  const [disclosuresData, setDisclosuresData] = useState({});

  // Load intake state
  useEffect(() => {
    fetch(`${API_URL}/api/compliance/intake/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('Intake link not found or expired.');
        return r.json();
      })
      .then(d => {
        setIntake(d.intake);
        setCaseSummary(d.case);
        setAdvisor(d.advisor);
        setClientData(d.intake.client_data || {});
        setFactFindData(d.intake.fact_find_data || {});
        setNeedsData(d.intake.needs_analysis_data || {});
        setInvestorData(d.intake.investor_profile_data || {});
        setDisclosuresData(d.intake.disclosures_data || {});
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  // Browser geolocation (audit trail at submit time)
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    setGeo(g => ({ ...g, status: 'pending' }));
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ status: 'granted', lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => setGeo({ status: 'denied', lat: null, lng: null, accuracy: null }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  const partialSave = useCallback(async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/intake/${token}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_data: clientData, fact_find_data: factFindData, needs_analysis_data: needsData, investor_profile_data: investorData, disclosures_data: disclosuresData }),
      });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  }, [token, clientData, factFindData, needsData, investorData, disclosuresData]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/compliance/intake/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_data: clientData,
          fact_find_data: factFindData,
          needs_analysis_data: needsData,
          investor_profile_data: investorData,
          disclosures_data: disclosuresData,
          geo_lat: geo.lat,
          geo_lng: geo.lng,
          geo_source: geo.status === 'granted' ? 'gps' : geo.status,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed.');
      }
      setSubmitted(true);
    } catch (e) {
      alert(e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center" data-testid="intake-error">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Available</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-500 mt-4">Please contact your advisor for a new link.</p>
        </CardContent></Card>
      </div>
    );
  }

  const status = intake?.status;
  if (submitted || status === 'submitted' || status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center" data-testid="intake-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Thank you, {intake?.client_name}!</h2>
          <p className="text-gray-600 mb-3">Your intake has been received. {status === 'accepted' ? 'Your advisor has accepted your information.' : 'Your advisor will review and confirm your information shortly.'}</p>
          <p className="text-sm text-gray-500">A copy has been forwarded to {advisor?.name || 'your advisor'}.</p>
        </CardContent></Card>
      </div>
    );
  }
  if (status === 'expired' || status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center" data-testid="intake-error">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link {status === 'expired' ? 'Expired' : 'Cancelled'}</h2>
          <p className="text-gray-600">This intake link is no longer valid. Please contact your advisor for a new link.</p>
        </CardContent></Card>
      </div>
    );
  }

  const updateClient = (k, v) => setClientData(prev => ({ ...prev, [k]: v }));
  const updateFF = (k, v) => setFactFindData(prev => ({ ...prev, [k]: v }));
  const updateNA = (k, v) => setNeedsData(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#003D5C] text-white py-4 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold truncate">Utmost Financial Inc.</h1>
            <p className="text-xs sm:text-sm text-[#D4AF37]">Self-Serve Intake — {caseSummary?.case_number}</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0">
            <Lock className="h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Secure Link</span><span className="sm:hidden">Secure</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Welcome */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <p className="text-gray-700 text-sm sm:text-base">Hello <span className="font-semibold" data-testid="intake-client-name">{intake?.client_name}</span>,</p>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Your advisor <strong>{advisor?.name || 'Utmost Advisor'}</strong> has prepared a brief intake form so you can share
              the information needed to plan your insurance file at your own pace. The form is divided into a few short sections — feel
              free to save your progress and come back later. <b>You can always go back and edit any field before final submit.</b>
            </p>
          </CardContent>
        </Card>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-1 sm:gap-2" data-testid="intake-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`flex-1 text-center text-xs sm:text-sm font-medium rounded-full py-2 px-1 ${i === step ? 'bg-[#003D5C] text-white' : i < step ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
              <span className="hidden sm:inline">{i + 1}. </span>{s.label}
            </div>
          ))}
        </div>

        {/* Step content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{STEPS[step].label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {STEPS[step].id === 'client' && <ClientStep data={clientData} update={updateClient} />}
            {STEPS[step].id === 'disclosures' && <DisclosuresStep data={disclosuresData} update={(k, v) => setDisclosuresData(p => ({ ...p, [k]: v }))} advisor={advisor} token={token} />}
            {STEPS[step].id === 'fact_find' && <FactFindStep data={factFindData} update={updateFF} hasSpouse={clientData.include_other_insureds || !!clientData.spouse_name} />}
            {STEPS[step].id === 'needs_analysis' && <NeedsStep data={needsData} update={updateNA} />}
            {STEPS[step].id === 'investor_profile' && <InvestorStep data={investorData} update={(k, v) => setInvestorData(p => ({ ...p, [k]: v }))} />}
            {STEPS[step].id === 'review' && <ReviewStep clientData={clientData} factFindData={factFindData} needsData={needsData} investorData={investorData} disclosuresData={disclosuresData} />}
          </CardContent>
        </Card>

        {/* Geo + Nav */}
        <div className="text-xs text-gray-500 flex items-center gap-2" data-testid="intake-geo-status">
          <Shield className="h-3.5 w-3.5" />
          {geo.status === 'pending' && <span>Requesting location for the audit trail…</span>}
          {geo.status === 'granted' && <span>Location captured (±{Math.round(geo.accuracy || 0)} m)</span>}
          {geo.status === 'denied' && <span>Location not shared — your IP and timestamp will still be recorded.</span>}
          {(geo.status === 'idle' || geo.status === 'unsupported') && <span>Your IP and timestamp will be recorded on submit.</span>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} data-testid="intake-prev-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <Button variant="outline" onClick={partialSave} disabled={saving} className="border-[#D4AF37] text-[#003D5C]" data-testid="intake-save-btn">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save Progress'}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => { partialSave(); setStep(s => s + 1); }} className="bg-[#0077B6] text-white" data-testid="intake-next-btn">
              Next<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] font-semibold" data-testid="intake-submit-btn">
              {submitting ? 'Submitting…' : 'Submit to my Advisor'}
            </Button>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 pb-6">
          Dominion Lending Centres Utmost Financial · Your information is encrypted and only visible to your advisor.
        </p>
      </div>
    </div>
  );
};

// ===========================================================================
// Step components — kept compact and inline
// ===========================================================================

const F = ({ label, type = 'text', value, onChange, placeholder, testid, ...rest }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input type={type} value={value ?? ''} onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" placeholder={placeholder} data-testid={testid} {...rest} />
  </div>
);

const ClientStep = ({ data, update }) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600">Confirm or update your personal details.</p>
    <div className="grid sm:grid-cols-2 gap-3">
      <F label="Full Legal Name *" value={data.full_name} onChange={(v) => update('full_name', v)} testid="intake-full-name" />
      <F label="Date of Birth" type="date" value={data.date_of_birth} onChange={(v) => update('date_of_birth', v)} />
      <div>
        <label className="block text-xs font-medium mb-1">Gender</label>
        <select value={data.gender || ''} onChange={(e) => update('gender', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
          <option value="">Select…</option>
          <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Marital Status</label>
        <select value={data.marital_status || ''} onChange={(e) => update('marital_status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
          <option value="">Select…</option>
          <option value="single">Single</option><option value="married">Married</option>
          <option value="common-law">Common-law</option><option value="divorced">Divorced</option>
          <option value="widowed">Widowed</option>
        </select>
      </div>
      <F label="Phone *" type="tel" value={data.phone} onChange={(v) => update('phone', v)} testid="intake-phone" />
      <F label="Email *" type="email" value={data.email} onChange={(v) => update('email', v)} testid="intake-email" />
      <F label="Address" value={data.address} onChange={(v) => update('address', v)} />
      <F label="City" value={data.city} onChange={(v) => update('city', v)} />
      <F label="Province" value={data.province} onChange={(v) => update('province', v)} />
      <F label="Postal Code" value={data.postal_code} onChange={(v) => update('postal_code', v)} />
      <F label="Occupation" value={data.occupation} onChange={(v) => update('occupation', v)} />
      <F label="Employer" value={data.employer} onChange={(v) => update('employer', v)} />
      <F label="Annual Income ($)" type="number" min="0" value={data.annual_income} onChange={(v) => update('annual_income', v)} />
      <F label="Social Insurance Number (SIN)" value={data.sin} onChange={(v) => update('sin', v)} testid="intake-sin" />
      <div>
        <label className="block text-xs font-medium mb-1">Smoker Status</label>
        <select value={data.smoker_status || 'non-smoker'} onChange={(e) => update('smoker_status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
          <option value="non-smoker">Non-smoker</option>
          <option value="smoker">Smoker</option>
          <option value="former-smoker">Former smoker</option>
        </select>
      </div>
    </div>

    {/* Iter 73 — products the client is applying for */}
    <div className="border-t pt-4">
      <h4 className="font-semibold text-sm mb-1">Which product(s) are you applying for?</h4>
      <p className="text-xs text-gray-600 mb-3">Select all that apply — your advisor will confirm the details with you.</p>
      <ProductSelector
        value={data.products_applied || []}
        onChange={(v) => update('products_applied', v)}
        other={data.products_other || ''}
        onOtherChange={(v) => update('products_other', v)}
        testidPrefix="intake-product"
      />
    </div>

    {/* Iter 31/32: explicit Yes/No gate for spouse / other insureds */}
    <div className="bg-amber-50 border border-amber-200 rounded p-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={!!data.include_other_insureds} onChange={(e) => update('include_other_insureds', e.target.checked)} className="mt-1 w-4 h-4" data-testid="intake-include-other" />
        <span className="text-sm text-gray-800">
          <b>Are you married, in a common-law relationship, or do you want other individuals on this application?</b>
          <br />
          <span className="text-xs text-gray-600">If checked, we'll ask a few questions about your spouse / partner.</span>
        </span>
      </label>
    </div>

    {!!data.include_other_insureds && (
      <div className="border-t pt-4">
        <h4 className="font-semibold text-sm mb-3">Spouse / Partner Information</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Spouse Full Name" value={data.spouse_name} onChange={(v) => update('spouse_name', v)} />
          <F label="Spouse Date of Birth" type="date" value={data.spouse_date_of_birth} onChange={(v) => update('spouse_date_of_birth', v)} />
          <F label="Spouse Phone" type="tel" value={data.spouse_phone} onChange={(v) => update('spouse_phone', v)} />
          <F label="Spouse Email" type="email" value={data.spouse_email} onChange={(v) => update('spouse_email', v)} />
          <F label="Spouse Occupation" value={data.spouse_occupation} onChange={(v) => update('spouse_occupation', v)} />
          <F label="Spouse Annual Income ($)" type="number" min="0" value={data.spouse_income} onChange={(v) => update('spouse_income', v)} />
          <F label="Spouse Location / Address" value={data.spouse_address} onChange={(v) => update('spouse_address', v)} testid="intake-spouse-address" />
          <F label="Spouse SIN" value={data.spouse_sin} onChange={(v) => update('spouse_sin', v)} testid="intake-spouse-sin" />
        </div>
      </div>
    )}
  </div>
);

const FactFindStep = ({ data, update, hasSpouse }) => (
  <div className="space-y-5">
    <p className="text-sm text-gray-600">Share your household's financial picture. All amounts in CAD.</p>

    <div>
      <h4 className="font-semibold text-sm mb-2">Assets</h4>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Cash & Savings" type="number" min="0" value={data.cash_savings} onChange={(v) => update('cash_savings', v)} />
        <F label="RRSP" type="number" min="0" value={data.rrsp} onChange={(v) => update('rrsp', v)} />
        <F label="TFSA" type="number" min="0" value={data.tfsa} onChange={(v) => update('tfsa', v)} />
        <F label="Non-Registered Investments" type="number" min="0" value={data.non_registered} onChange={(v) => update('non_registered', v)} />
        <F label="Real Estate (Equity)" type="number" min="0" value={data.real_estate_value} onChange={(v) => update('real_estate_value', v)} />
      </div>
    </div>

    <div>
      <h4 className="font-semibold text-sm mb-2">Liabilities</h4>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Mortgage Balance" type="number" min="0" value={data.mortgage_balance} onChange={(v) => update('mortgage_balance', v)} />
        <F label="Credit Cards" type="number" min="0" value={data.credit_cards} onChange={(v) => update('credit_cards', v)} />
        <F label="Loans" type="number" min="0" value={data.loans} onChange={(v) => update('loans', v)} />
        <F label="Line of Credit" type="number" min="0" value={data.line_of_credit} onChange={(v) => update('line_of_credit', v)} />
      </div>
    </div>

    <div>
      <h4 className="font-semibold text-sm mb-2">Existing Insurance Coverage</h4>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Group Life (through employer)" type="number" min="0" value={data.group_life} onChange={(v) => update('group_life', v)} />
        <F label="Individual Life Insurance" type="number" min="0" value={data.individual_life} onChange={(v) => update('individual_life', v)} />
        <F label="Mortgage Insurance" type="number" min="0" value={data.mortgage_insurance} onChange={(v) => update('mortgage_insurance', v)} />
        <F label="Critical Illness" type="number" min="0" value={data.critical_illness} onChange={(v) => update('critical_illness', v)} />
      </div>
    </div>

    {hasSpouse && (
      <div className="border-t pt-4">
        <h4 className="font-semibold text-sm mb-2">Spouse / Partner Finances (Optional)</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Spouse RRSP" type="number" min="0" value={data.spouse_rrsp} onChange={(v) => update('spouse_rrsp', v)} />
          <F label="Spouse TFSA" type="number" min="0" value={data.spouse_tfsa} onChange={(v) => update('spouse_tfsa', v)} />
          <F label="Spouse Group Life" type="number" min="0" value={data.spouse_group_life} onChange={(v) => update('spouse_group_life', v)} />
          <F label="Spouse Individual Life" type="number" min="0" value={data.spouse_individual_life} onChange={(v) => update('spouse_individual_life', v)} />
        </div>
      </div>
    )}

    <div>
      <h4 className="font-semibold text-sm mb-2">Budget</h4>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Desired Monthly Premium" type="number" min="0" value={data.desired_monthly_premium} onChange={(v) => update('desired_monthly_premium', v)} />
        <F label="Maximum Affordable Premium" type="number" min="0" value={data.maximum_affordable_premium} onChange={(v) => update('maximum_affordable_premium', v)} />
      </div>
    </div>
  </div>
);

const NEED_OBJECTIVES = [
  { value: 'protect_family', label: 'Protect my family if I pass away' },
  { value: 'cover_mortgage', label: 'Pay off my mortgage' },
  { value: 'income_replacement', label: 'Replace my income' },
  { value: 'final_expenses', label: 'Cover final expenses & burial' },
  { value: 'education_fund', label: 'Fund kids\' education' },
  { value: 'business_protection', label: 'Protect my business / key person' },
  { value: 'estate_planning', label: 'Estate planning / wealth transfer' },
  { value: 'critical_illness', label: 'Cover a critical illness diagnosis' },
  { value: 'disability', label: 'Income protection if I can\'t work' },
];

const NeedsStep = ({ data, update }) => {
  const toggleObj = (val) => {
    const cur = data.objectives || [];
    update('objectives', cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]);
  };
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">Tell us what you'd like your insurance to do for you. Pick all that apply.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {NEED_OBJECTIVES.map(o => (
          <label key={o.value} className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${ (data.objectives || []).includes(o.value) ? 'bg-blue-50 border-blue-300' : 'border-gray-200' }`}>
            <input type="checkbox" checked={(data.objectives || []).includes(o.value)} onChange={() => toggleObj(o.value)} className="mt-1" data-testid={`intake-obj-${o.value}`} />
            <span className="text-sm">{o.label}</span>
          </label>
        ))}
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Anything specific you want to share with your advisor?</label>
        <textarea rows="4" value={data.client_notes || ''} onChange={(e) => update('client_notes', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. 'I had cancer 5 years ago — fully recovered', 'I'm planning to switch jobs', etc." data-testid="intake-client-notes" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Approximate desired coverage amount" type="number" min="0" value={data.desired_coverage} onChange={(v) => update('desired_coverage', v)} placeholder="e.g. 500000" />
        <F label="How many years of income would you like to replace?" type="number" min="0" value={data.income_replacement_years} onChange={(v) => update('income_replacement_years', v)} placeholder="10" />
      </div>
    </div>
  );
};

const DISCLOSURES = [
  {
    key: 'advisor_disclosure',
    pdfKey: 'advisor_disclosure',
    title: 'Advisor Disclosure Statement',
    body: (advisor) => `${advisor?.name || 'Your advisor'} is a licensed life insurance advisor${advisor?.license_number ? ` (Licence #${advisor.license_number})` : ''} contracted to offer products from multiple insurers. The advisor may receive commissions and other compensation from the insurer whose product you purchase. You may request details of this compensation at any time.`,
  },
  {
    key: 'privacy_consent',
    pdfKey: 'privacy_consent',
    title: 'Privacy Disclosure & Consent',
    body: () => 'We collect, use and disclose your personal information solely to assess your needs, recommend and arrange insurance coverage, and service your file. Your information is kept confidential and shared only with the insurer(s) and parties necessary to process your application, in accordance with PIPEDA. You may withdraw consent or request access to your information at any time.',
  },
  {
    key: 'electronic_communications',
    pdfKey: 'electronic_consent',
    title: 'Electronic Communications Consent',
    body: () => 'You consent to receive documents, disclosures and communications electronically (by email and secure link), and to sign documents electronically. Electronic documents have the same legal effect as paper. You may request paper copies or withdraw this consent at any time.',
  },
  {
    key: 'conflict_disclosure',
    pdfKey: 'conflict_disclosure',
    title: 'Conflict of Interest Disclosure',
    body: () => 'Your advisor is required to identify and disclose any actual or potential conflicts of interest and to always place your interests first. Where the advisor represents multiple insurers, product recommendations are based on your needs — not on the level of compensation offered.',
  },
];

const DisclosuresStep = ({ data, update, advisor, token }) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600">Please review the following disclosures and acknowledge each one to continue. Click <b>“Read the full document”</b> to open the complete PDF.</p>
    {DISCLOSURES.map(d => (
      <div key={d.key} className="border rounded-lg p-3 bg-gray-50">
        <h4 className="font-semibold text-sm text-[#003D5C] mb-1">{d.title}</h4>
        <p className="text-xs text-gray-600 leading-relaxed">{d.body(advisor)}</p>
        <a
          href={`${API_URL}/api/compliance/intake/${token}/disclosure/${d.pdfKey}`}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-[#0077B6] hover:underline mt-2"
          data-testid={`intake-disc-pdf-${d.key}`}
        >
          📄 Read the full document (PDF)
        </a>
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input type="checkbox" checked={!!data[d.key]} onChange={(e) => update(d.key, e.target.checked)} data-testid={`intake-disc-${d.key}`} />
          <span className="text-sm font-medium">I have read and acknowledge this disclosure</span>
        </label>
      </div>
    ))}
  </div>
);

const INVESTOR_FIELDS = [
  {
    key: 'investment_knowledge', label: 'How would you describe your investment knowledge?',
    desc: 'This tells us how much investing experience you have.',
    options: ['None', 'Limited', 'Good', 'Extensive'],
    guide: {
      None: 'No investing experience (e.g., you only use a savings account).',
      Limited: 'You know the basics (e.g., RRSP/TFSA, GICs).',
      Good: 'You understand stocks, bonds and mutual funds.',
      Extensive: 'You actively manage a diversified portfolio.',
    },
  },
  {
    key: 'time_horizon', label: 'What is your investment time horizon?',
    desc: 'How long before you plan to use this money.',
    options: ['Less than 3 years', '3 to 10 years', '10+ years'],
    guide: {
      'Less than 3 years': 'Short term — e.g., saving for a car or a home down payment.',
      '3 to 10 years': 'Medium term — e.g., a child’s education.',
      '10+ years': 'Long term — e.g., retirement.',
    },
  },
  {
    key: 'risk_tolerance', label: 'What is your risk tolerance?',
    desc: 'How comfortable you are if your investment value goes up and down.',
    options: ['Low', 'Medium', 'High'],
    guide: {
      Low: 'You prefer safety and don’t want to lose your original money (e.g., GICs).',
      Medium: 'You accept some ups and downs for higher returns (e.g., balanced funds).',
      High: 'You can handle large swings for maximum growth (e.g., equity funds).',
    },
  },
  {
    key: 'investment_objective', label: 'What is your primary investment objective?',
    desc: 'What you mainly want your money to do.',
    options: ['Safety', 'Income', 'Growth', 'Aggressive Growth'],
    guide: {
      Safety: 'Protect your money above all.',
      Income: 'Earn steady income (e.g., interest or dividends).',
      Growth: 'Grow your money over time.',
      'Aggressive Growth': 'Maximize growth, accepting high risk.',
    },
  },
  {
    key: 'income_range', label: 'What is your annual income range?',
    desc: 'Your approximate total yearly household income before taxes.',
    options: ['Under $50,000', '$50,000 - $100,000', '$100,000 - $150,000', '$150,000 - $250,000', 'Over $250,000'],
  },
];

const InvestorStep = ({ data, update }) => (
  <div className="space-y-5">
    <p className="text-sm text-gray-600">A few quick questions to help your advisor understand your investor profile. Not sure which to pick? The short notes under each option explain what they mean.</p>
    {INVESTOR_FIELDS.map(f => (
      <div key={f.key}>
        <label className="block text-sm font-medium">{f.label}</label>
        {f.desc && <p className="text-xs text-gray-400 mb-1">{f.desc}</p>}
        <select value={data[f.key] || ''} onChange={(e) => update(f.key, e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid={`intake-investor-${f.key}`}>
          <option value="">Select…</option>
          {f.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {f.guide && (
          <ul className="mt-1.5 space-y-0.5">
            {f.options.map(o => (
              <li key={o} className={`text-[11px] leading-snug ${data[f.key] === o ? 'text-[#0077B6] font-medium' : 'text-gray-400'}`}>
                <span className="font-semibold">{o}:</span> {f.guide[o]}
              </li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

const ReviewStep = ({ clientData, factFindData, needsData, investorData, disclosuresData }) => (
  <div className="space-y-4 text-sm">
    <p className="text-gray-600">Please review your answers before submitting. Your advisor will reach out shortly to confirm.</p>
    <div>
      <h4 className="font-semibold mb-1">About You</h4>
      <div className="bg-gray-50 border rounded p-3 grid sm:grid-cols-2 gap-2 text-xs">
        <div><b>Name:</b> {clientData.full_name || '—'}</div>
        <div><b>Email:</b> {clientData.email || '—'}</div>
        <div><b>Phone:</b> {clientData.phone || '—'}</div>
        <div><b>DOB:</b> {clientData.date_of_birth || '—'}</div>
        <div><b>Marital:</b> {clientData.marital_status || '—'}</div>
        <div><b>Smoker:</b> {clientData.smoker_status || '—'}</div>
        {clientData.spouse_name && <div className="sm:col-span-2"><b>Spouse:</b> {clientData.spouse_name}</div>}
      </div>
    </div>
    <div>
      <h4 className="font-semibold mb-1">Disclosures Acknowledged</h4>
      <div className="bg-gray-50 border rounded p-3 text-xs">
        {DISCLOSURES.map(d => (
          <div key={d.key}>{disclosuresData[d.key] ? '✓' : '✗'} {d.title}</div>
        ))}
      </div>
    </div>
    <div>
      <h4 className="font-semibold mb-1">Your Finances (totals)</h4>
      <div className="bg-gray-50 border rounded p-3 grid sm:grid-cols-2 gap-2 text-xs">
        <div><b>Cash & savings:</b> ${(factFindData.cash_savings || 0).toLocaleString()}</div>
        <div><b>RRSP+TFSA:</b> ${((factFindData.rrsp || 0) + (factFindData.tfsa || 0)).toLocaleString()}</div>
        <div><b>Mortgage:</b> ${(factFindData.mortgage_balance || 0).toLocaleString()}</div>
        <div><b>Existing life cover:</b> ${((factFindData.group_life || 0) + (factFindData.individual_life || 0)).toLocaleString()}</div>
        <div><b>Desired premium:</b> ${(factFindData.desired_monthly_premium || 0).toLocaleString()}/mo</div>
      </div>
    </div>
    <div>
      <h4 className="font-semibold mb-1">Your Goals</h4>
      <div className="bg-gray-50 border rounded p-3 text-xs">
        <b>Selected:</b> {(needsData.objectives || []).join(', ') || '—'}
        {needsData.client_notes && <p className="mt-2 italic">"{needsData.client_notes}"</p>}
        {needsData.desired_coverage > 0 && <p className="mt-1"><b>Desired coverage:</b> ${needsData.desired_coverage.toLocaleString()}</p>}
      </div>
    </div>
    <div>
      <h4 className="font-semibold mb-1">Investor Profile</h4>
      <div className="bg-gray-50 border rounded p-3 grid sm:grid-cols-2 gap-2 text-xs">
        {INVESTOR_FIELDS.map(f => (
          <div key={f.key}><b>{f.label.replace(/\?$/, '').replace(/^(How would you describe your |What is your )/i, '')}:</b> {investorData[f.key] || '—'}</div>
        ))}
      </div>
    </div>
    <p className="text-xs text-gray-500 pt-2 border-t">
      By clicking <b>Submit to my Advisor</b>, you confirm the information above is accurate to the best of your knowledge.
      Your advisor will follow up to confirm details before any policy decisions are made.
    </p>
  </div>
);

export default ClientIntake;
